import { Tooltip, Button } from "prism-react";
import React, { useEffect } from "react";

import { useAnalytics } from "hooks/useAnalytics";

import type { InspectorApi } from "./useInspectorApi";

export const InspectorButton: React.FC<{
  inspectorApi: InspectorApi;
  isAiPrompt?: boolean;
  isAiAvailable?: boolean;
}> = ({ inspectorApi, isAiPrompt, isAiAvailable }) => {
  const {
    inspectorAvailable,
    inspectorLoading,
    inspectorEnabled,
    setInspectorEnabled,
  } = inspectorApi;
  const track = useAnalytics();

  const [isEnabled, setIsEnabled] = React.useState(false);

  useEffect(() => {
    const handleKey = (evt: KeyboardEvent) => {
      if (inspectorEnabled && evt.key === "Escape") {
        setInspectorEnabled(false);
      }
    };

    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("keydown", handleKey);
    };
  }, [inspectorEnabled, setInspectorEnabled]);

  useEffect(() => {
    if (isEnabled && !inspectorEnabled) {
      setIsEnabled(false);
    }
  }, [isEnabled, inspectorEnabled]);

  let inspectorTooltipMessage: string | React.ReactNode = isAiPrompt
    ? "Prompt Boxy to suggest code"
    : "Component inspector";

  if (inspectorLoading) {
    inspectorTooltipMessage = "Inspector is loading...";
  } else if (!inspectorAvailable) {
    inspectorTooltipMessage = "This page doesn't support inspecting";
  } else if (!isAiAvailable && isAiPrompt) {
    inspectorTooltipMessage = (
      <span>
        AI component editing is available with CodeSandbox Pro.{" "}
        <a
          href="https://codesandbox.io/pro"
          rel="noreferrer noopener"
          target="_blank"
        >
          Upgrade here.
        </a>
      </span>
    );
  }

  return (
    <Tooltip content={inspectorTooltipMessage}>
      <Button
        css={{
          "svg, &:hover svg, &:focus svg": {
            color: isEnabled ? "$accent-primary-base" : undefined,
          },
        }}
        disabled={!inspectorAvailable || (isAiPrompt && !isAiAvailable)}
        icon={isAiPrompt ? "sparkleFill" : "inspect"}
        onClick={() => {
          if (inspectorEnabled) {
            setInspectorEnabled(false);
          } else {
            track("open_inspector", { event_source: "editor" });

            setIsEnabled(true);
            setInspectorEnabled(true, ["prism-react"], isAiPrompt);
          }
        }}
      />
    </Tooltip>
  );
};
